import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '@src/components/Field/Field';
import { showToast } from '@src/components/Toast';
import { classes } from '@src/components/layout/space';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './CopyLink.css';

export interface CopyLinkProps {
  label: string;
  link: string;
  /** Shown under the box before the link is copied. */
  copyHint: string;
  /** Shown under the box after a successful copy. */
  copiedHint: string;
  disabled?: boolean;
  onCopied?: () => void;
  testId?: string;
}

/**
 * A link the organizer copies with one click. Looks like a field but is a
 * button — the value is read-only and copying is the only action.
 */
export function CopyLink({
  label,
  link,
  copyHint,
  copiedHint,
  disabled = false,
  onCopied,
  testId,
}: CopyLinkProps) {
  const { t } = useTranslation();
  const controlId = useId();
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const url = link.trim();
  const inactive = disabled || !url;

  const copied = copiedUrl === url;

  async function copy() {
    if (inactive) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      onCopied?.();
    } catch {
      setCopiedUrl(null);
      showToast(t('box.organizer.copyFailed'));
    }
  }

  return (
    <Field
      controlId={controlId}
      label={label}
      hint={copied ? copiedHint : inactive ? undefined : copyHint}
      disabled={inactive}
      className={classes('copy-link', copied && 'copy-link--copied')}
      testId={testId}
    >
      <button
        type="button"
        id={controlId}
        className="copy-link__button"
        disabled={inactive}
        title={inactive ? undefined : copyHint}
        onClick={() => void copy()}
        {...testIdAttr(testIdFor(testId, 'control'))}
      >
        <Typography as="span" role="meta/mono" className="copy-link__value">
          {url}
        </Typography>
      </button>
    </Field>
  );
}
